/**
 * Form Definition API Client
 * CRUD operations for form definitions — mirrors old atlas-form-sdk pattern.
 */

import { BaseHttpClient } from './http.client';
import { atlasApiConfig } from './config';
import { convertKeysToPascalCase } from './request-transformer';
import { createGuid } from '@bizfirst/common-js';
import type { ApiResponse, FormDefinition, GetFullFormByFormIDResponse } from '@atlas-forms/types-js';

export interface ListFormsRequest {
  PageInfo?: {
    PageNumber?: number;
    PageSize?: number;
    SortBy?: string;
    SortOrder?: 'asc' | 'desc';
  };
  SearchTitle?: string;
  // TODO: FormCode search - backend /list endpoint doesn't support FormCode filtering.
  // Use dedicated /by-code endpoint instead when needed. Deferred for future enhancement.
  // FormCode?: string;      // Search by form code
  FormID?: number;        // Search by form ID - matches backend parameter name
  IsEnabled?: boolean;
  CreatedFrom?: string;
  CreatedTo?: string;
  CategoryId?: number;
  /** True = ExecutionNode config-panel Forms only, False = real/end-user Forms only (excludes
   *  node-config Forms), undefined = no filter. See backend FormListRequest.IsNodeForm's own
   *  doc comment for why this matters: every ExecutionNode's design-time config panel is itself
   *  stored as a Form row, so an unfiltered search can return rows like
   *  "ElasticsearchNode_Document_Create" instead of the real Forms a caller is looking for. */
  IsNodeForm?: boolean;
}

export interface ListFormsResponse {
  Data: FormDefinition[];
  PageInfo: {
    PageNumber: number;
    PageSize: number;
    TotalPages: number;
    TotalRecords: number;
  };
}

const EP = {
  LIST:       '/api/v1/atlas/forms/list',
  ENABLED:    '/api/v1/atlas/forms/enabled',
  GET_BY_ID:  '/api/v1/atlas/forms/by-id',
  FULL_FORM:  '/api/v1/atlas/forms/full-form',
  CREATE:     '/api/v1/atlas/forms',
  UPDATE:     '/api/v1/atlas/forms/update',
  DELETE:     (id: number) => `/api/v1/atlas/forms/${id}`,
} as const;

export class FormDefinitionApiClient extends BaseHttpClient {
  constructor(config?: { baseUrl?: string; timeout?: number; token?: string; tenantId?: number }) {
    super({
      baseUrl: config?.baseUrl ?? atlasApiConfig.baseUrl,
      timeout: config?.timeout ?? atlasApiConfig.timeout,
      token: config?.token ?? atlasApiConfig.token,
      tenantId: config?.tenantId ?? atlasApiConfig.tenantId,
    });
  }

  async listForms(request: ListFormsRequest = {}): Promise<ApiResponse<ListFormsResponse>> {
    // Build request with PageInfo and filters
    const pageInfo = request.PageInfo ?? { PageNumber: 1, PageSize: 10 };
    const body: Record<string, unknown> = {
      PageInfo: pageInfo,
    };

    // Add optional filter fields
    if (request.SearchTitle !== undefined) {
      body.SearchTitle = request.SearchTitle;
    }
    // TODO: FormCode - backend /list doesn't support it, commented out pending enhancement
    // if (request.FormCode !== undefined) {
    //   body.FormCode = request.FormCode;
    // }
    if (request.FormID !== undefined) {
      body.FormID = request.FormID;
    }
    if (request.IsEnabled !== undefined) {
      body.IsEnabled = request.IsEnabled;
    }
    if (request.IsNodeForm !== undefined) {
      body.IsNodeForm = request.IsNodeForm;
    }
    if (request.CreatedFrom !== undefined) {
      body.CreatedFrom = request.CreatedFrom;
    }
    if (request.CreatedTo !== undefined) {
      body.CreatedTo = request.CreatedTo;
    }
    if (request.CategoryId !== undefined) {
      body.CategoryId = request.CategoryId;
    }

    return this.postQuery<ListFormsResponse>(EP.LIST, body);
  }

  async listEnabledForms(request: ListFormsRequest = {}): Promise<ApiResponse<ListFormsResponse>> {
    // Build request with PageInfo and filters (automatically filters IsEnabled=true)
    const pageInfo = request.PageInfo ?? { PageNumber: 1, PageSize: 10 };
    const body: Record<string, unknown> = {
      PageInfo: pageInfo,
      IsEnabled: true, // Force enabled filter
    };

    // Add optional filter fields
    if (request.SearchTitle !== undefined) {
      body.SearchTitle = request.SearchTitle;
    }
    if (request.FormID !== undefined) {
      body.FormID = request.FormID;
    }
    if (request.CreatedFrom !== undefined) {
      body.CreatedFrom = request.CreatedFrom;
    }
    if (request.CreatedTo !== undefined) {
      body.CreatedTo = request.CreatedTo;
    }
    if (request.CategoryId !== undefined) {
      body.CategoryId = request.CategoryId;
    }

    return this.postQuery<ListFormsResponse>(EP.ENABLED, body);
  }

  async getFormById(formID: number): Promise<ApiResponse<FormDefinition>> {
    // Add cache-busting parameter to force fresh data
    return this.postQuery<FormDefinition>(EP.GET_BY_ID, {
      ID: { ID: formID },
      // Cache buster to ensure fresh data from backend
      _cacheBuster: Date.now()
    });
  }

  async getFormByResId(resId: string): Promise<ApiResponse<FormDefinition>> {
    return this.postQuery<FormDefinition>(EP.GET_BY_ID, { ID: { ResID: resId } });
  }

  async getFormByName(name: string): Promise<ApiResponse<FormDefinition>> {
    return this.postQuery<FormDefinition>(EP.GET_BY_ID, { Name: name });
  }

  async getFullFormByFormID(formID: number): Promise<ApiResponse<GetFullFormByFormIDResponse>> {
    return this.postQuery<GetFullFormByFormIDResponse>(EP.FULL_FORM, { FormID: formID });
  }

  async createForm(request: Record<string, unknown>): Promise<ApiResponse<FormDefinition>> {
    return this.post<FormDefinition>(EP.CREATE, request);
  }

  async updateForm(id: number, request: Record<string, unknown>): Promise<ApiResponse<FormDefinition>> {
    // Convert to PascalCase and add FormID
    const requestData = convertKeysToPascalCase({ ...request, FormID: { ID: id } });

    // POST /update endpoint: metadata as string, properties at top level
    const metadata = JSON.stringify({
      RequestId: createGuid(),
      CorrelationId: createGuid(),
      Timestamp: new Date().toISOString(),
      Version: '1.0',
    });
    const body = JSON.stringify({ metadata, ...(requestData as Record<string, unknown>) });

    return this.postRaw<FormDefinition>(EP.UPDATE, body);
  }

  async deleteForm(id: number): Promise<ApiResponse<void>> {
    return this.delete<void>(EP.DELETE(id), { ID: { ID: id } });
  }
}

export const formDefinitionApiClient = new FormDefinitionApiClient();
